import React, { useState, useEffect, useMemo } from 'react';
import { useNavigation } from '../context/NavigationContext';
import { jwtDecode } from 'jwt-decode';
import { useLocation, useNavigate } from 'react-router-dom';

import languageLearningApi from './language_learning/middleware/languageLearningApi';

const GoalSetting = ({ token }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentCourse } = useNavigation();
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [currentGoal, setCurrentGoal] = useState(null);
  const [committed, setCommitted] = useState(false);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [todayProgress, setTodayProgress] = useState(null);
  const [hoverIndex, setHoverIndex] = useState(null);
  const [courseId, setCourseId] = useState(null);

  // Parse courseId from URL parameters
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const courseIdParam = params.get('courseId');
    if (courseIdParam) {
      setCourseId(courseIdParam);
    }
  }, [location, currentCourse]);

  // Goal options - memoized to avoid recreation on each render
  // Issue 1 fix: Renamed goal options to clearly indicate daily learning commitment

  const GOAL_DESCRIPTION_SUFFIX = 'new sentences + all associated vocab'

  const goalOptions = useMemo(() => [
    {
      id: 'beginner',
      title: 'Casual',
      icon: '🧘‍♂️',
      description: `2 ${GOAL_DESCRIPTION_SUFFIX}`,
      sentenceCount: 2
    },
    {
      id: 'regular',
      title: 'Normal',
      icon: '📚',
      description: `4 ${GOAL_DESCRIPTION_SUFFIX}`,
      sentenceCount: 4,
      recommended: true
    },
    {
      id: 'advanced',
      title: 'Serious',
      icon: '✍️',
      description: `6 ${GOAL_DESCRIPTION_SUFFIX}`,
      sentenceCount: 6
    },
    {
      id: 'expert',
      title: 'Monk',
      icon: '🧘‍♀️',
      description: `8 ${GOAL_DESCRIPTION_SUFFIX}`,
      sentenceCount: 8
    }
  ], []); // Empty dependency array since this data is static

  // Get user data from token and fetch existing goal if any
  useEffect(() => {
    const effectiveCourseId = courseId || (currentCourse ? (currentCourse.id || currentCourse._id) : null);
    if (!token || !effectiveCourseId) return;

    const fetchData = async () => {
      try {
        const decoded = jwtDecode(token);
        setUserData(decoded.user);

        if (decoded.user && decoded.user._id) {
          try {
            // Updated API endpoint as requested
            const goalResponse = await languageLearningApi.get(`/goals/goals/${decoded.user._id}/${effectiveCourseId}`);

            if (goalResponse.data && goalResponse.data.goal) {
              // Find the goal option that matches the saved goal
              const existingGoal = goalOptions.find(
                option => option.sentenceCount === goalResponse.data.goal.sentenceCountForGoal
              );

              if (existingGoal) {
                setSelectedGoal(existingGoal);
                setCurrentGoal(existingGoal); // Set current goal for the horizontal card
              }

              // Also fetch today's progress
              try {
                const progressResponse = await languageLearningApi.get(`/progress/${decoded.user._id}/${effectiveCourseId}`);
                if (progressResponse.data && progressResponse.data.progress) {
                  setTodayProgress(progressResponse.data.progress);
                }
              } catch (progressErr) {
                console.log('Error fetching today\'s progress:', progressErr);
              }
            }
          } catch (fetchErr) {
            // It's fine if there's no existing goal, this just means it's a new user
            console.log('No existing goal found, this may be a new user');
          }
        }

        setLoading(false);
      } catch (err) {
        setError('Failed to decode user token');
        setLoading(false);
      }
    };

    fetchData();
  }, [token, currentCourse, courseId, goalOptions]);

  // Handle goal selection - Issue 2 fix: Added ability to deselect a goal
  const handleSelectGoal = (goal) => {
    if (selectedGoal && selectedGoal.id === goal.id) {
      // If clicking the same goal, deselect it
      setSelectedGoal(null);
    } else {
      // Otherwise, select the new goal
      setSelectedGoal(goal);
    }
  };

  // Handle commit button click
  const handleCommit = async () => {
    if (!selectedGoal) return;

    try {
      // Use courseId from URL or currentCourse
      const effectiveCourseId = courseId || (currentCourse ? (currentCourse._id || currentCourse.id) : null);
      if (!effectiveCourseId) {
        setError('No course ID available');
        return;
      }

      const response = await languageLearningApi.post('/goals/goals', {
        userId: userData._id,
        courseId: effectiveCourseId,
        sentenceCountForGoal: selectedGoal.sentenceCount
      });

      if (response.status === 200) {
        setCommitted(true);
        navigate(`/language-dashboard/${effectiveCourseId}`);
      } else {
        setError('Failed to save your goal. Please try again.');
      }
    } catch (err) {
      setError('Error: ' + (err.response?.data?.message || err.message));
    }
  };

  // All styles merged into the component
  const styles = {
    goalSettingContainer: {
      maxWidth: '900px',
      margin: '0 auto',
      padding: '30px',
      fontFamily: "'Roboto', sans-serif",
    },
    goalSettingTitle: {
      fontSize: '32px',
      textAlign: 'center',
      marginBottom: '10px',
      color: '#333',
    },
    goalSettingSubtitle: {
      fontSize: '18px',
      textAlign: 'center',
      marginBottom: '30px',
      color: '#666',
    },
    goalCardsContainer: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '20px',
      marginBottom: '30px',
    },
    goalCard: (isHovered, isSelected, isRecommended) => ({
      backgroundColor: isSelected ? 'rgba(76, 175, 80, 0.1)' : '#fff',
      borderRadius: '12px',
      padding: '20px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      position: 'relative',
      boxShadow: isSelected
        ? '0 15px 30px rgba(76, 175, 80, 0.2)'
        : (isHovered ? '0 15px 30px rgba(0, 0, 0, 0.15)' : '0 10px 20px rgba(0, 0, 0, 0.1)'),
      transform: (isSelected || isHovered) ? 'translateY(-5px)' : 'translateY(0)',
      border: isSelected ? '2px solid #4CAF50' : 'none',
    }),
    goalIcon: {
      fontSize: '48px',
      marginBottom: '15px',
    },
    // Issue 3 fix: Fixed heading alignment by ensuring consistent height
    goalTitle: {
      fontSize: '20px',
      fontWeight: '600',
      marginBottom: '10px',
      color: '#333',
      height: '50px', // Fixed height for title container
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    goalDescription: {
      fontSize: '14px',
      color: '#666',
      minHeight: '42px', // Ensure descriptions have minimum height
    },
    recommendedBadge: {
      position: 'absolute',
      top: '-10px',
      right: '-10px',
      backgroundColor: '#FF9800',
      color: 'white',
      padding: '5px 10px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: '600',
      boxShadow: '0 4px 8px rgba(255, 152, 0, 0.3)',
    },
    noteContainer: {
      backgroundColor: '#f8f9fa',
      borderRadius: '8px',
      padding: '20px',
      marginBottom: '30px',
    },
    noteText: {
      fontSize: '14px',
      color: '#555',
      marginBottom: '10px',
    },
    commitButton: (isDisabled) => ({
      display: 'block',
      width: '100%',
      maxWidth: '400px',
      margin: '0 auto',
      backgroundColor: isDisabled ? '#cccccc' : '#4CAF50',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      padding: '15px 20px',
      fontSize: '16px',
      fontWeight: '600',
      cursor: isDisabled ? 'not-allowed' : 'pointer',
      transition: 'all 0.3s ease',
    }),
    congratulationsContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '80vh',
    },
    congratulationsCard: {
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '40px',
      textAlign: 'center',
      boxShadow: '0 15px 30px rgba(0, 0, 0, 0.1)',
      maxWidth: '500px',
    },
    congratulationsTitle: {
      fontSize: '28px',
      color: '#333',
      marginBottom: '20px',
    },
    congratulationsText: {
      fontSize: '16px',
      color: '#666',
      marginBottom: '10px',
    },
    returnButton: {
      marginTop: '30px',
      backgroundColor: '#4CAF50',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      padding: '12px 24px',
      fontSize: '16px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
    progressContainer: {
      margin: '20px 0 30px',
      padding: '15px',
      backgroundColor: '#f8f9fa',
      borderRadius: '8px',
      textAlign: 'center',
    },
    progressTitle: {
      marginBottom: '10px',
      color: '#333',
    },
    progressBarContainer: {
      height: '20px',
      backgroundColor: '#e9ecef',
      borderRadius: '10px',
      overflow: 'hidden',
      marginBottom: '10px',
    },
    progressBar: (width) => ({
      height: '100%',
      width: `${width}%`,
      backgroundColor: '#4CAF50',
      borderRadius: '10px',
      transition: 'width 0.5s ease',
    }),
    loading: {
      textAlign: 'center',
      padding: '50px',
      fontSize: '18px',
    },
    error: {
      textAlign: 'center',
      padding: '50px',
      fontSize: '18px',
      color: '#dc3545',
    },
    // New styles for current goal horizontal row card
    currentGoalContainer: {
      backgroundColor: '#f0f8ff',
      borderRadius: '12px',
      padding: '20px',
      marginBottom: '30px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      boxShadow: '0 5px 15px rgba(0, 0, 0, 0.1)',
    },
    currentGoalTitle: {
      fontSize: '20px',
      fontWeight: '600',
      marginBottom: '15px',
      color: '#333',
      textAlign: 'center',
    },
    currentGoalContent: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '20px',
      flexWrap: 'wrap',
    },
    currentGoalIcon: {
      fontSize: '48px',
    },
    currentGoalInfo: {
      textAlign: 'center',
    },
    currentGoalName: {
      fontSize: '18px',
      fontWeight: '600',
      marginBottom: '5px',
      color: '#333',
    },
    currentGoalDescription: {
      fontSize: '14px',
      color: '#666',
    },
  };

  if (loading) {
    return <div style={styles.loading}>Loading...</div>;
  }

  if (error) {
    return <div style={styles.error}>{error}</div>;
  }

  if (committed) {
    return (
      <div style={styles.congratulationsContainer}>
        <div style={styles.congratulationsCard}>
          <h2 style={styles.congratulationsTitle}>🎉 Congratulations, {userData.name}! 🎉</h2>
          <p style={styles.congratulationsText}>You've successfully committed to your learning goal.</p>
          <p style={styles.congratulationsText}>Remember, consistency is key to success in language learning.</p>
          <button
            onClick={() => {
              const effectiveCourseId = courseId || (currentCourse ? (currentCourse._id || currentCourse.id) : null);
              navigate(`/language-dashboard/${effectiveCourseId}`);
            }}
            style={styles.returnButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#3e8e41';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 5px 15px rgba(76, 175, 80, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#4CAF50';
              e.currentTarget.style.transform = 'none';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            Start Learning Now
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.goalSettingContainer}>
      <h1 style={styles.goalSettingTitle}>Choose Your Daily Learning Commitment</h1>
      <p style={styles.goalSettingSubtitle}>Select how many new items you want to learn each day</p>

      {/* Display current goal in a horizontal row card */}
      {currentGoal && (
        <div style={styles.currentGoalContainer}>
          <h3 style={styles.currentGoalTitle}>Your Current Daily Commitment</h3>
          <div style={styles.currentGoalContent}>
            <div style={styles.currentGoalIcon}>{currentGoal.icon}</div>
            <div style={styles.currentGoalInfo}>
              <div style={styles.currentGoalName}>{currentGoal.title}</div>
              <div style={styles.currentGoalDescription}>{currentGoal.description}</div>
            </div>
          </div>
        </div>
      )}

      <div style={styles.goalCardsContainer}>
        {goalOptions.map((goal, index) => (
          <div
            key={goal.id}
            style={styles.goalCard(
              hoverIndex === index,
              selectedGoal && selectedGoal.id === goal.id,
              goal.recommended
            )}
            onClick={() => handleSelectGoal(goal)}
            onMouseEnter={() => setHoverIndex(index)}
            onMouseLeave={() => setHoverIndex(null)}
          >
            <div style={styles.goalIcon}>{goal.icon}</div>
            <h3 style={styles.goalTitle}>{goal.title}</h3>
            <p style={styles.goalDescription}>{goal.description}</p>
            {goal.recommended && <div style={styles.recommendedBadge}>Recommended</div>}
          </div>
        ))}
      </div>

      {todayProgress && selectedGoal && (
        <div style={styles.progressContainer}>
          <h3 style={styles.progressTitle}>Today's Progress</h3>
          <div style={styles.progressBarContainer}>
            <div
              style={styles.progressBar(
                Math.max(0, 100 - (todayProgress.sentencesRemaining / selectedGoal.sentenceCount * 100))
              )}
            ></div>
          </div>
          <p>
            {selectedGoal.sentenceCount - todayProgress.sentencesRemaining} of {selectedGoal.sentenceCount} sentences completed
            {todayProgress.sentencesRemaining === 0 ? ' - Goal Completed!' : ''}
          </p>
        </div>
      )}

      <div style={styles.noteContainer}>
        <p style={styles.noteText}>
          <strong>Note:</strong> Complete existing reviews before learning new items. Our SRS system quizzes you on concepts to build long-term memory.
        </p>
        <p style={styles.noteText}>
          Until you're done with all your reviews, you cannot learn new items!
        </p>
        <p style={{ ...styles.noteText, marginBottom: 0 }}>
          <strong>Choose a manageable workload</strong> - remember, consistency is the key - Rome wasn't built in a day.
        </p>
      </div>

      <button
        style={styles.commitButton(!selectedGoal)}
        disabled={!selectedGoal}
        onClick={handleCommit}
        onMouseEnter={(e) => {
          if (!selectedGoal) return;
          e.currentTarget.style.backgroundColor = '#3e8e41';
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 5px 15px rgba(76, 175, 80, 0.3)';
        }}
        onMouseLeave={(e) => {
          if (!selectedGoal) return;
          e.currentTarget.style.backgroundColor = '#4CAF50';
          e.currentTarget.style.transform = 'none';
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        {todayProgress ? 'Update Learning Commitment' : 'Commit to Learning Goal'}
      </button>
    </div>
  );
};

export default GoalSetting;